import { applyVspProjectInWells, useAppDispatch } from '@core/core/store'
import type { ProjectId, WellId } from 'common/types'
import type { FC } from 'react'
import { useState } from 'react'

import {
  extractFilterFormContext,
  extractVspIdContext,
  Footer,
  TableWithFilter,
  useStepController,
  useStepControllerMethods,
} from '../../components'
import type { OnSetFilterFormFunc, RowWellVsp } from '../../types'
import { ChosenControl } from './ChosenControl'

type StepChooseTargetWellsProps = {
  projectId: ProjectId
}

export const StepChooseTargetWells: FC<StepChooseTargetWellsProps> = ({ projectId }) => {
  const [checkedWell, setCheckedWell] = useState(new Set<WellId>())
  const { activeStep } = useStepController()
  const { setContextToActiveStep, addNextStep } = useStepControllerMethods()

  const vspId = extractVspIdContext(activeStep)
  const form = extractFilterFormContext(activeStep)

  const dispatch = useAppDispatch()

  const saveContext: OnSetFilterFormFunc = form => {
    setContextToActiveStep('chooseTargetWells', { ...form, vspId })
  }

  const handleCheck = (wellId: WellId) => {
    if (checkedWell.has(wellId)) {
      setCheckedWell(prev => {
        prev.delete(wellId)
        return new Set(prev)
      })
    } else {
      setCheckedWell(prev => new Set(prev.add(wellId)))
    }
  }

  const handleReset = () => {
    setCheckedWell(new Set())
  }

  const handleApply = () => {
    if (vspId && form?.fieldId) {
      const wellsToAttach = [...checkedWell]
      dispatch(
        applyVspProjectInWells(
          projectId,
          { vspId, wellIds: wellsToAttach },
          form.fieldId,
          activeStep.label,
        ),
      )
      addNextStep('', 'applyVsp', wellsToAttach)
    }
  }

  const additionalControlsRenderer = (rows: RowWellVsp[]) => {
    const handleChooseAll = () => {
      setCheckedWell(new Set(rows.map(row => row.id)))
    }

    return (
      <ChosenControl
        chosenCount={checkedWell.size}
        onChooseAll={handleChooseAll}
        onReset={handleReset}
      />
    )
  }

  return (
    <>
      <TableWithFilter
        prompt="Выберите скважины, куда хотите применить скоростной закон"
        hasNextStep={false}
        saveContext={saveContext}
        additionalControls={additionalControlsRenderer}
        checkedWells={checkedWell}
        onCheck={handleCheck}
      />

      <Footer disabled={checkedWell.size === 0} onApply={handleApply} />
    </>
  )
}
